import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Download, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Skeleton,
  cn,
} from '@clarix/ui';
import { CodeConfirmDialog, downloadBackupCodesPdf } from '@clarix/ui-shared';
import { useAuthStore } from '@clarix/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import {
  useTwoFactorStatus,
  useSetupTwoFactor,
  useEnableTwoFactor,
  useDisableTwoFactor,
  getApiErrorMessage,
  queryKeys,
} from '@/core/api';

/** Formats an ISO date to a friendly "Jan 5, 2026" label. */
function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Two-factor authentication management for the signed-in user's profile.
 * Reads the current status, and enables (setup → confirm code → backup codes) or
 * disables 2FA (confirm with a current code) via self-contained dialogs. Uses the
 * persisted session — the api-client attaches the token, so no token is threaded
 * here (unlike the forced login-time enrolment, which carries a limited token).
 */
export function TwoFactorManager() {
  const status = useTwoFactorStatus();

  if (status.isLoading) return <Skeleton className="h-10 w-full rounded-md" />;

  if (status.isError || !status.data) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-xl">Couldn&apos;t load your 2FA status.</p>
        <Button variant="outline" size="sm" onClick={() => void status.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const { enabled, enrolled_at, backup_codes_remaining } = status.data;

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={cn(
            'flex h-8 w-8 flex-none items-center justify-center rounded-md',
            enabled ? 'bg-primary/10 text-primary' : 'bg-surface text-muted-foreground',
          )}
        >
          {enabled ? (
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
          ) : (
            <ShieldOff className="h-4 w-4" strokeWidth={1.5} />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-foreground text-xl font-medium">Authenticator app</p>
            <Badge variant={enabled ? 'default' : 'outline'}>{enabled ? 'On' : 'Off'}</Badge>
          </div>
          <p className="text-muted-foreground mt-0.5 text-lg">
            {enabled
              ? `Enabled ${formatDate(enrolled_at)} · ${backup_codes_remaining} backup code${
                  backup_codes_remaining === 1 ? '' : 's'
                } left`
              : 'Add a second step at sign-in with an authenticator app.'}
          </p>
        </div>
      </div>

      <div className="flex-none">
        {enabled ? <DisableTwoFactorDialog /> : <EnableTwoFactorDialog />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

type EnablePhase = 'enrolling' | 'backup-codes';

function EnableTwoFactorDialog() {
  const qc = useQueryClient();
  const setup = useSetupTwoFactor();
  const enable = useEnableTwoFactor();
  const email = useAuthStore((s) => s.user?.email);

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<EnablePhase>('enrolling');
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  // Fetch a fresh secret each time the dialog opens; reset everything on open.
  // This runs from the trigger's Radix onOpenChange (an event handler), not an
  // effect — so the setup call is guaranteed to fire and setState is allowed.
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    setPhase('enrolling');
    setSecret('');
    setOtpauthUrl('');
    setCode('');
    setBackupCodes([]);
    setSaved(false);
    setup.reset();
    enable.reset();
    setup
      .mutateAsync(undefined)
      .then((res) => {
        setSecret(res.secret);
        setOtpauthUrl(res.otpauth_url);
      })
      .catch(() => {
        /* surfaced in the dialog */
      });
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await enable.mutateAsync({ code: code.trim() });
      setBackupCodes(res.backup_codes);
      setPhase('backup-codes');
      void qc.invalidateQueries({ queryKey: queryKeys.twoFactorStatus() });
    } catch {
      /* surfaced below */
    }
  };

  const savePdf = () => {
    if (!downloadBackupCodesPdf({ codes: backupCodes, accountLabel: email })) return;
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const setupErr = setup.error ? getApiErrorMessage(setup.error, "Couldn't start setup.") : null;
  const enableErr = enable.error
    ? getApiErrorMessage(enable.error, "Couldn't verify that code. Please try again.")
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">Enable</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {phase === 'enrolling' ? (
          <form onSubmit={confirm} className="flex flex-1 flex-col">
            <DialogHeader>
              <DialogTitle>Enable two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan the QR code in your authenticator app, then confirm with a 6-digit code.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                <div className="flex h-[150px] w-[150px] flex-none items-center justify-center rounded-lg bg-white">
                  {setup.isPending ? (
                    <Skeleton className="h-[126px] w-[126px]" />
                  ) : (
                    otpauthUrl && <QRCodeSVG value={otpauthUrl} size={126} />
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-muted-foreground text-sm font-medium uppercase tracking-wide">
                    Manual entry key
                  </p>
                  <code className="text-foreground block break-all text-lg">{secret || '—'}</code>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="enable-2fa-code">6-digit code</Label>
                <Input
                  id="enable-2fa-code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {setupErr && <p className="text-destructive text-xl">{setupErr}</p>}
                {enableErr && <p className="text-destructive text-xl">{enableErr}</p>}
              </div>
            </DialogBody>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={enable.isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={enable.isPending || setup.isPending || code.trim().length !== 6}
              >
                {enable.isPending ? 'Verifying…' : 'Verify & enable'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-1 flex-col">
            <DialogHeader>
              <DialogTitle>Save your backup codes</DialogTitle>
              <DialogDescription>
                Each code works once and they are shown only now. Download the PDF and keep it
                somewhere safe.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <ul className="bg-surface grid grid-cols-2 gap-1.5 rounded-lg p-3 font-mono text-xl">
                {backupCodes.map((c) => (
                  <li key={c} className="text-foreground tabular-nums">
                    {c}
                  </li>
                ))}
              </ul>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" className="gap-1.5" onClick={savePdf}>
                {saved ? (
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                ) : (
                  <Download className="h-4 w-4" strokeWidth={1.5} />
                )}
                {saved ? 'Downloaded' : 'Download PDF'}
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                I&apos;ve saved them — done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------

function DisableTwoFactorDialog() {
  const disable = useDisableTwoFactor();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-destructive"
        onClick={() => {
          disable.reset();
          setOpen(true);
        }}
      >
        Disable
      </Button>
      <CodeConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Disable two-factor authentication"
        description="Enter a current code from your authenticator app to turn 2FA off. Your account will be less protected."
        confirmLabel="Disable 2FA"
        loadingLabel="Disabling…"
        variant="destructive"
        loading={disable.isPending}
        error={disable.error ? getApiErrorMessage(disable.error, "Couldn't disable 2FA. Please try again.") : null}
        onConfirm={async (code) => {
          try {
            await disable.mutateAsync({ code });
            setOpen(false);
          } catch {
            /* surfaced via the dialog's error prop */
          }
        }}
      />
    </>
  );
}
